import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { Observable, catchError, lastValueFrom, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { rewardsApplicationLoggerToken } from "../../../tokens";
import { getLoadingStatus } from "../../../utils/statuses";
import RewardsBoostCampaignApiService from "../api_service/RewardsBoostCampaignApiService";
import type { BoostCampaignResponseType } from "../models/RewardsBoostCampaignEntities";
import RewardsBoostCampaignStore from "../store/RewardsBoostCampaignStore";

@singleton()
class RewardsBoostCampaignService {
    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly boostCampaignApiService: RewardsBoostCampaignApiService,
        private readonly boostCampaignStore: RewardsBoostCampaignStore
    ) {}

    //Api calls
    getActiveBoostCampaign(): Observable<BoostCampaignResponseType> {
        this.logger.debug(
            "RewardsBoostCampaignService.getActiveBoostCampaign is started"
        );

        const status = this.boostCampaignStore.getStatus();
        this.boostCampaignStore.setStatus(getLoadingStatus(status));

        return this.boostCampaignApiService.getActiveBoostCampaign().pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsBoostCampaignService.getActiveBoostCampaign is errored",
                    error
                );
                this.boostCampaignStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug(
                    "RewardsBoostCampaignService.getActiveBoostCampaign is ready"
                );
                //Store Update
                applyTransaction(() => {
                    this.boostCampaignStore.update((state) => ({
                        ...state,
                        boostCampaign: value.boostCampaign,
                    }));
                    this.boostCampaignStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetActiveBoostCampaign(): Promise<void> {
        if (this.boostCampaignStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getActiveBoostCampaign());
    }
}

export default RewardsBoostCampaignService;
